import Big from 'big.js';
import { RangePeriod } from 'constants/dashboardChart';
import {
  format,
  fromUnixTime,
  getUnixTime,
  isAfter,
  startOfDay,
  subDays,
} from 'date-fns';
import _, { range } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { getRewardsPaidOut } from 'services/userService';
import { appendFilUnit, getFormattedFilAmountFromString } from 'services/utilsService';
import { RequestState } from 'types/common';
import { RewardsPayout } from 'types/user';

type Data = {
  amount: string;
  createdAt: number;
};

const sevenDaysBefore = () => startOfDay(subDays(new Date(), 7));
const thirtyDaysBefore = () => startOfDay(subDays(new Date(), 30));
const EARLIEST_REWARDS_DATE = new Date(2022, 0, 1);

type GetDateRange = (rangePeriod: RangePeriod) => {
  from: Date;
  to: Date;
};
export const getDateRange: GetDateRange = (rangePeriod) => {
  switch (rangePeriod) {
    case 'LastSevenDays':
      return {
        from: sevenDaysBefore(),
        to: new Date(),
      };
    case 'LastThirtyDays':
      return {
        from: thirtyDaysBefore(),
        to: new Date(),
      };
    case 'AllTime':
      return {
        from: EARLIEST_REWARDS_DATE,
        to: new Date(),
      };
    default:
      return {
        from: sevenDaysBefore(),
        to: new Date(),
      };
  }
};

export const formatYAxis = (value: number): string => {
  return appendFilUnit(value);
};

type formatTime = (rangePeriod: RangePeriod) => string;
export const formatTime: formatTime = (rangePeriod) => {
  switch (rangePeriod) {
    case 'LastSevenDays':
      return 'dd MMM';
    case 'LastThirtyDays':
      return 'dd MMM';
    case 'AllTime':
      return 'MMM yyyy';
    default:
      return 'MMM';
  }
};

export type ChartData = {
  [minerId: string]: Data[];
};
type DashboardChartDataMapper = (payment: RewardsPayout[]) => ChartData;
export const dashboardChartDataMapper: DashboardChartDataMapper = (payments) =>
  _.mapValues(
    _.groupBy(payments, (payment) => payment.minerId),
    (payments) => {
      return payments.map((payment) => {
        const amount = getFormattedFilAmountFromString(payment.amount);

        return {
          amount: amount.ok ? amount.value : '0',
          createdAt: getUnixTime(new Date(payment.createdAt)),
        };
      });
    },
  );

type UseGetDashboardChartData = () => {
  chartData: ChartData;
  timeFormat: string;
  dateRange: RangePeriod;
  setDateRange: Dispatch<SetStateAction<RangePeriod>>;
  setSelectedChartId: Dispatch<SetStateAction<string>>;
  selectedChartId: string;
  requestState: RequestState;
  maxAmount: number;
};
export const useGetDashboardChartData: UseGetDashboardChartData = () => {
  const [requestState, setRequestState] = useState<RequestState>({ status: 'Initial' });
  const [dateRange, setDateRange] = useState<RangePeriod>('LastSevenDays');
  const [chartData, setChartData] = useState<ChartData>({});
  const [selectedChartId, setSelectedChartId] = useState('');
  const [maxAmount, setMaxAmount] = useState<number>(0);

  const timeFormat = useMemo(() => formatTime(dateRange), [dateRange]);

  const getMaxRewardAmount = (rewards: RewardsPayout[]) => {
    const rewardWithMaxValue = rewards.reduce((maxReward, reward) => {
      const current = Big(reward.amount);
      const currentMax = Big(maxReward.amount);

      return current.gt(currentMax) ? reward : maxReward;
    });

    const formatedFil = getFormattedFilAmountFromString(rewardWithMaxValue.amount);
    const maxAmount = formatedFil.ok ? Number(formatedFil.value) : Infinity;
    const maxRoundAmount = Math.ceil((maxAmount + 1) / 10) * 10;

    return maxRoundAmount;
  };

  useEffect(() => {
    const { from, to } = getDateRange(dateRange);

    const data = getRewardsPaidOut({ range: dateRange, from, to });
    setRequestState({ status: 'InProgress' });

    data
      .then(({ results }) => {
        if (results.length === 0) {
          setChartData({});
          setRequestState({ status: 'Success' });

          return;
        }

        const maxRewardAmount = getMaxRewardAmount(results);
        setMaxAmount(maxRewardAmount);

        const chartData = dashboardChartDataMapper(results);
        setChartData(dashboardChartDataMapper(results));
        setSelectedChartId(Object.keys(chartData)[0]);
        setRequestState({ status: 'Success' });
      })
      .catch((error) => {
        setRequestState({ status: 'Error', error: error.message });
      });
  }, [dateRange]);

  return {
    chartData,
    timeFormat,
    dateRange,
    requestState,
    maxAmount,
    setDateRange,
    selectedChartId,
    setSelectedChartId,
  };
};

type mockPayload = {
  dateRange: number;
  rewardIncrease: number;
  numberOfRewards?: number;
};
const randomInteger = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};
const mockChartResults = ({
  dateRange,
  rewardIncrease,
  numberOfRewards = 10,
}: mockPayload) => {
  const startDate = getUnixTime(subDays(new Date(), dateRange + 1));
  const endDate = getUnixTime(subDays(new Date(), 1));
  const delta = Math.floor((endDate - startDate) / numberOfRewards);
  const minerId = (Math.random() + 1).toString(36).substring(2);

  const rangeArray = range(0, numberOfRewards);
  let amount = 0;
  const rewards = rangeArray.map((index) => {
    amount = randomInteger(0, rewardIncrease);

    return {
      amount: String(amount),
      createdAt: fromUnixTime(startDate + delta * index).toISOString(),
      messageCid: minerId + index,
      minerId,
      allowanceId: '1',
    };
  });

  return rewards;
};

const mockChartData = (results: RewardsPayout[]) => ({
  count: results.length,
  limit: 100,
  offset: 0,
  results,
});

export const allTime = range(1, 4).flatMap(() =>
  mockChartResults({
    dateRange: 25,
    numberOfRewards: 8,
    rewardIncrease: 10000000000,
  }),
);

export const sevenDays = allTime.filter((data) =>
  isAfter(new Date(data.createdAt), sevenDaysBefore()),
);

export const thirtyDays = allTime.filter((data) =>
  isAfter(new Date(data.createdAt), thirtyDaysBefore()),
);

export const MockedChartData = {
  sevenDays: mockChartData(sevenDays),
  thirtyDays: mockChartData(thirtyDays),
  allTime: mockChartData(allTime),
};

export const xAxisTickFormatter = (unixTime: number): string => {
  return format(fromUnixTime(unixTime), 'dd.MM.');
};
